import { useMemo } from 'react'
import { usePlayers } from './usePlayers'
import { useMyFollows } from './useFollows'
import { useMyGroups } from './useGroups'
import { useAuth } from '../contexts/AuthContext'

/**
 * Shared player search/filter logic used by QuickRoundSetup, AddPlayersView, and GroupDetail.
 *
 * @param {Object} options
 * @param {Set} options.excludeIds - Player IDs to exclude from results (already-added players)
 * @param {string} options.searchText - Current search string
 * @returns {{ matchingFollowedPlayers, localSearchResults, matchingGroups, followedPlayerIds, allPlayers, myFollows, myGroups }}
 */
export function usePlayerSearch({ excludeIds, searchText }) {
  const { user } = useAuth()
  const { data: allPlayers } = usePlayers()
  const { data: myFollows } = useMyFollows()
  const { data: myGroups } = useMyGroups()

  const isSearching = searchText.length >= 1

  // Followed players not yet excluded
  const followedPlayers = useMemo(
    () => myFollows?.filter((p) => !excludeIds.has(p.id)) || [],
    [myFollows, excludeIds]
  )

  // Followed players matching search text
  const matchingFollowedPlayers = useMemo(() => {
    if (!isSearching) return []
    return followedPlayers.filter((p) => matchesSearch(p, searchText))
  }, [followedPlayers, searchText, isSearching])

  const followedUserIds = useMemo(
    () => new Set(matchingFollowedPlayers.map((p) => p.user_id).filter(Boolean)),
    [matchingFollowedPlayers]
  )
  const followedPlayerIds = useMemo(
    () => new Set(matchingFollowedPlayers.map((p) => p.id)),
    [matchingFollowedPlayers]
  )

  // Local search through all players (registered users + user's manual players)
  const localSearchResults = useMemo(() => {
    if (!isSearching) return []
    return (allPlayers || []).filter((p) => {
      if (excludeIds.has(p.id)) return false
      if (followedPlayerIds.has(p.id)) return false
      if (p.user_id && followedUserIds.has(p.user_id)) return false
      // Hide guest/manual players not created by this user
      if (!p.user_id && p.created_by !== user?.id) return false
      return matchesSearch(p, searchText)
    })
  }, [allPlayers, excludeIds, followedPlayerIds, followedUserIds, user?.id, searchText, isSearching])

  // Groups matching search text
  const matchingGroups = useMemo(() => {
    if (!isSearching) return []
    return (myGroups || []).filter((g) => {
      const q = searchText.toLowerCase()
      return g.name.toLowerCase().includes(q)
    })
  }, [myGroups, searchText, isSearching])

  return {
    matchingFollowedPlayers,
    localSearchResults,
    matchingGroups,
    followedPlayerIds,
    allPlayers,
    myFollows,
    myGroups,
    isSearching,
  }
}

function matchesSearch(player, searchText) {
  const q = searchText.toLowerCase()
  return player.first_name?.toLowerCase().startsWith(q) ||
         player.last_name?.toLowerCase().startsWith(q) ||
         player.email?.toLowerCase().startsWith(q) ||
         player.display_name?.toLowerCase().split(/\s+/).some(w => w.startsWith(q))
}
